import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Bug, Send, Loader2, CheckCircle2, Phone, ExternalLink, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { toast } from "sonner";

const SUPPORT_PHONE_DISPLAY = "0534 964 67 03";
const SUPPORT_PHONE_INTL = "905349646703"; // wa.me expects country-code, no plus
const SUPPORT_EMAIL = "milyemsoft@gmail.com";
const CATEGORIES = [
  { id: "general", label: "Genel Soru" },
  { id: "bug", label: "Hata Bildirimi" },
  { id: "billing", label: "Fatura / Ödeme" },
  { id: "feature", label: "Öneri" },
];

/**
 * Floating support balloon (bottom-right).
 * - Closed: pill "Destek" button with pulse
 * - Open menu: 2 primary actions (WhatsApp, Hata Bildir)
 * - Bug form: category + textarea, POST /support/report
 * - Silently hides on /signage (TV mode) so it never shows on public display screens.
 */
export default function SupportWidget() {
  const [mode, setMode] = useState("closed"); // closed | menu | form | done
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [ticketNo, setTicketNo] = useState("");
  const panelRef = useRef(null);

  // Click-outside to close (hook MUST run before any early return)
  useEffect(() => {
    if (mode === "closed") return;
    const onDown = (e) => {
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target)) setMode("closed");
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [mode]);

  // Hide on signage (TV mode) & auth screens
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const hiddenPaths = ["/signage", "/login", "/signup", "/forgot-password", "/auth/callback"];
  if (hiddenPaths.some((p) => path.startsWith(p))) return null;

  const openSupportWhatsApp = () => {
    openWhatsApp(
      SUPPORT_PHONE_INTL,
      "Merhaba, MİLYEMSOFT panelinden yazıyorum. Yardıma ihtiyacım var:"
    );
  };

  const submitReport = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Lütfen bir mesaj yazın");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/support/report", { category, message: message.trim() });
      setTicketNo(r.data.ticket_no || "");
      setMode("done");
      setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gönderilemedi, tekrar deneyin");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed z-[70] print:hidden"
      style={{
        right: "max(1.25rem, env(safe-area-inset-right))",
        bottom: "max(1.25rem, env(safe-area-inset-bottom))",
      }}
      data-testid="support-widget"
    >
      {mode === "closed" && (
        <button
          onClick={() => setMode("menu")}
          className="group relative w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center border shadow-[0_10px_30px_-8px_rgba(212,175,55,0.55)] hover:shadow-[0_16px_40px_-8px_rgba(212,175,55,0.75)] hover:scale-105 active:scale-95 transition-all duration-200"
          style={{
            background: "radial-gradient(circle at 30% 30%, #f4d976 0%, #d4af37 55%, #a4841e 100%)",
            borderColor: "rgba(212,175,55,0.6)",
          }}
          data-testid="support-widget-toggle"
          aria-label="Destek"
          title="Destek"
        >
          {/* Pulse ring — göz almadan varlığını hissettirir */}
          <span
            className="absolute inset-0 rounded-full animate-ping opacity-40"
            style={{ background: "rgba(212,175,55,0.4)" }}
          />
          <MessageCircle size={20} className="relative text-black" strokeWidth={2.2} />
        </button>
      )}

      {mode !== "closed" && (
        <div
          ref={panelRef}
          className="w-[calc(100vw-2rem)] max-w-[380px] border shadow-[0_30px_80px_-15px_rgba(0,0,0,0.7)] overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #0a0a0a 0%, #060606 100%)",
            borderColor: "rgba(212,175,55,0.3)",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8" style={{ background: "#050505" }}>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: "var(--gold)" }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "var(--gold)" }} />
              </span>
              <div>
                <div className="text-sm text-white">Yardım Merkezi</div>
                <div className="text-[10px] font-mono tracking-widest text-neutral-500 mt-0.5">
                  {mode === "menu" && "NASIL YARDIMCI OLABİLİRİZ?"}
                  {mode === "form" && "HATA / ÖNERİ BİLDİR"}
                  {mode === "done" && "TALEP KAYIT ALTINDA"}
                </div>
              </div>
            </div>
            <button
              onClick={() => setMode("closed")}
              className="text-neutral-500 hover:text-white p-1 -mr-1"
              aria-label="Kapat"
              data-testid="support-widget-close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Menu */}
          {mode === "menu" && (
            <div className="p-4 space-y-3">
              <button
                onClick={openSupportWhatsApp}
                className="w-full flex items-start gap-3 p-4 border border-white/10 hover:border-[--gold]/40 transition-colors text-left group"
                data-testid="support-whatsapp-btn"
              >
                <div className="shrink-0 w-10 h-10 flex items-center justify-center" style={{ background: "rgba(37,211,102,0.15)", border: "1px solid rgba(37,211,102,0.3)" }}>
                  <Phone size={16} style={{ color: "#25d366" }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-sm text-white">
                    Müşteri Temsilcisine Bağlan
                    <ExternalLink size={11} className="text-neutral-500" />
                  </div>
                  <div className="text-[11px] text-neutral-400 mt-0.5">WhatsApp · {SUPPORT_PHONE_DISPLAY}</div>
                  <div className="text-[10px] text-neutral-600 font-mono mt-1">Ort. yanıt süresi &lt; 10dk (mesai)</div>
                </div>
              </button>

              <button
                onClick={() => setMode("form")}
                className="w-full flex items-start gap-3 p-4 border border-white/10 hover:border-[--gold]/40 transition-colors text-left"
                data-testid="support-bugreport-btn"
              >
                <div className="shrink-0 w-10 h-10 flex items-center justify-center" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.3)" }}>
                  <Bug size={16} style={{ color: "var(--gold)" }} />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white">Hata Bildir</div>
                  <div className="text-[11px] text-neutral-400 mt-0.5">Uygulama içi tıkanma, veri sorunu, öneri</div>
                  <div className="text-[10px] text-neutral-600 font-mono mt-1">Panelden direkt kayıt açılır</div>
                </div>
              </button>

              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("MİLYEMSOFT · Destek Talebi")}&body=${encodeURIComponent("Merhaba MilyemSoft ekibi,\n\nDestek talebim:\n\n")}`}
                className="w-full flex items-start gap-3 p-4 border border-white/10 hover:border-[--gold]/40 transition-colors text-left group"
                data-testid="support-email-btn"
              >
                <div className="shrink-0 w-10 h-10 flex items-center justify-center" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.3)" }}>
                  <Mail size={16} style={{ color: "var(--gold)" }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-sm text-white">
                    Bize E-posta Gönder
                    <ExternalLink size={11} className="text-neutral-500" />
                  </div>
                  <div className="text-[11px] text-neutral-400 mt-0.5">{SUPPORT_EMAIL}</div>
                  <div className="text-[10px] text-neutral-600 font-mono mt-1">Konu başlığı hazır olarak açılır</div>
                </div>
              </a>

              <Link
                to="/iletisim"
                className="block text-center text-[11px] font-mono tracking-wider text-neutral-500 hover:text-neutral-300 pt-1"
                onClick={() => setMode("closed")}
              >
                RESMİ İLETİŞİM BİLGİLERİ →
              </Link>
            </div>
          )}

          {/* Form */}
          {mode === "form" && (
            <form onSubmit={submitReport} className="p-4 space-y-3" data-testid="support-form">
              <div>
                <div className="label-caps mb-1.5">Kategori</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCategory(c.id)}
                      className={`text-[11px] font-mono tracking-wider py-2 border transition-colors ${
                        category === c.id
                          ? "text-black"
                          : "text-neutral-300 border-white/10 hover:border-white/25"
                      }`}
                      style={category === c.id ? { background: "var(--gold)", borderColor: "var(--gold)" } : {}}
                      data-testid={`support-cat-${c.id}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="label-caps mb-1.5">Mesajınız</div>
                <textarea
                  required
                  rows={5}
                  autoFocus
                  maxLength={4000}
                  placeholder="Sorunu adım adım tarif edin. Kullandığınız cihaz, tarayıcı ve gördüğünüz hata mesajını da yazarsanız çok yardımı olur."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-transparent border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] resize-none"
                  data-testid="support-message"
                />
                <div className="text-[10px] font-mono text-neutral-600 text-right mt-1">
                  {message.length} / 4000
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setMode("menu")}
                  className="text-[11px] font-mono tracking-wider text-neutral-500 hover:text-white px-3 py-2"
                >
                  ← GERİ
                </button>
                <button
                  type="submit"
                  disabled={sending || !message.trim()}
                  className="btn-primary-gold flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-xs disabled:opacity-60"
                  data-testid="support-submit"
                >
                  {sending ? <><Loader2 size={13} className="animate-spin" /> GÖNDERİLİYOR</> : <><Send size={13} /> GÖNDER</>}
                </button>
              </div>
            </form>
          )}

          {/* Done */}
          {mode === "done" && (
            <div className="p-6 text-center space-y-3" data-testid="support-done">
              <div className="w-14 h-14 mx-auto flex items-center justify-center" style={{ background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.4)" }}>
                <CheckCircle2 size={26} className="text-emerald-400" />
              </div>
              <div className="text-white text-base">Talebiniz alındı</div>
              {ticketNo && (
                <div className="inline-block px-3 py-1.5 border border-white/10 font-mono text-xs tracking-widest text-neutral-300">
                  KAYIT NO: <span style={{ color: "var(--gold)" }}>#{ticketNo}</span>
                </div>
              )}
              <p className="text-xs text-neutral-400 leading-relaxed">
                En kısa sürede size dönüş yapılacak. Kayıt numaranızı not almanız hızlı takip için faydalı olabilir.
              </p>
              <button
                type="button"
                onClick={() => { setMode("closed"); setTicketNo(""); }}
                className="btn-primary-gold px-6 py-2 text-xs mt-2"
                data-testid="support-done-close"
              >
                Kapat
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
